import { useState } from "react";
import { PledgePanel } from "./PledgePanel";
import { Plus, ShieldCheck, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { money } from "../../lib/api";

const ED_LINK = "https://ope.ed.gov/dapip/#/home";
const empty = { name: "", accreditor: "", city: "", state: "", website: "", programs: [], description: "", registration: "", verify_status: "verify", pledge_org_id: "", gift_route: "", kind: "school", active: true };
const FIELDS = [["name", "School name"], ["accreditor", "Accrediting agency"], ["city", "City / campuses"], ["state", "State (2 letters)"], ["website", "Website"], ["registration", "Verification note / OPEID"]];

const SchoolCard = ({ c, onEdit, onDelete }) => (
  <div data-testid={`school-card-${c.id}`} className="lux-card p-6">
    <div className="flex justify-between gap-2">
      <h3 className="font-serif-lux text-2xl">{c.name}</h3>
      {!c.active && <span className="text-[10px] font-mono-lux uppercase text-slate-500">Hidden</span>}
    </div>
    <p className="text-xs text-slate-400 mt-1">{[c.city, c.state].filter(Boolean).join(", ")}</p>
    <p data-testid={`school-verify-${c.id}`} className={`text-xs mt-3 inline-flex items-center gap-1 ${c.verify_status === "verified" ? "text-emerald-300" : "text-amber-300"}`}>
      {c.verify_status === "verified" ? <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> : <ShieldAlert className="w-3.5 h-3.5 shrink-0" />}
      {c.verify_status === "verified" ? (c.kind === "fund" ? "Nonprofit verified" : "Accreditation verified") : "Verify accreditation"} · {c.accreditor || "accreditor not set"}
    </p>
    {c.programs?.length > 0 && <p className="text-xs text-slate-500 mt-2">{c.programs.join(" · ")}</p>}
    <p data-testid={`school-pledge-${c.id}`} className={`text-[11px] mt-2 ${c.pledge_org_id ? "text-emerald-300" : "text-slate-500"}`}>{!c.active ? "Inactive" : c.pledge_org_id ? "Linked to Pledge" : "Not linked to Pledge"}{c.gift_route ? ` · ${c.gift_route}` : ""}</p>
    <p className="font-mono-lux text-sm text-[#38BDF8] mt-4">{money(c.total_received)} in scholarships</p>
    <div className="mt-4 flex gap-2">
      <button data-testid={`admin-edit-school-${c.id}`} onClick={() => onEdit(c)} className="btn-ghost px-4 py-2 text-xs">Edit</button>
      <button data-testid={`admin-delete-school-${c.id}`} onClick={() => window.confirm("Remove this school?") && onDelete(c.id)} className="btn-ghost px-4 py-2 text-xs text-red-300">Remove</button>
    </div>
  </div>
);

export const CharityManager = ({ items, onSave, onDelete, onReload }) => {
  const [f, setF] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const { id, total_received, created_at, ...body } = f;
    await onSave(id, { ...body, programs: typeof body.programs === "string" ? body.programs.split(",").map((s) => s.trim()).filter(Boolean) : body.programs });
    setF(null);
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        <button data-testid="admin-add-school-btn" onClick={() => setF(empty)} className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" />Add accredited school</button>
        <a href={ED_LINK} target="_blank" rel="noreferrer" data-testid="admin-ed-dapip-link" className="text-xs text-[#38BDF8] hover:underline">Check accreditation in the US Dept. of Education database →</a>
      </div>
      <PledgePanel schools={items} onLinked={() => onReload?.()} />
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => <SchoolCard key={c.id} c={c} onEdit={setF} onDelete={onDelete} />)}
      </div>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="bg-[#13192B] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="school-form-dialog">
          <DialogHeader><DialogTitle className="font-serif-lux text-3xl font-medium">{f?.id ? "Edit school" : "Add school"}</DialogTitle></DialogHeader>
          {f && (
            <form onSubmit={submit} className="space-y-4">
              {FIELDS.map(([k, l]) => (
                <label key={k} className="block"><span className="text-xs text-slate-400">{l}</span>
                  <input data-testid={`school-form-${k}`} required={k === "name"} value={f[k] || ""} onChange={set(k)} className="lux-input mt-1" /></label>
              ))}
              <label className="block"><span className="text-xs text-slate-400">Programs (comma separated)</span>
                <input data-testid="school-form-programs" value={Array.isArray(f.programs) ? f.programs.join(", ") : f.programs} onChange={set("programs")} className="lux-input mt-1" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-400">Pledge nonprofit ID</span>
                  <input data-testid="school-form-pledge" value={f.pledge_org_id || ""} onChange={set("pledge_org_id")} className="lux-input mt-1" /></label>
                <label className="block"><span className="text-xs text-slate-400">Type</span>
                  <select data-testid="school-form-kind" value={f.kind || "school"} onChange={set("kind")} className="lux-input mt-1">
                    <option value="school">Accredited school</option><option value="fund">Nonprofit scholarship fund</option>
                  </select></label>
                <label className="block"><span className="text-xs text-slate-400">Gift route</span>
                  <input data-testid="school-form-gift-route" value={f.gift_route || ""} onChange={set("gift_route")} placeholder="College foundation via Pledge" className="lux-input mt-1" /></label>
              </div>
              <label className="block"><span className="text-xs text-slate-400">Description</span>
                <textarea data-testid="school-form-description" value={f.description || ""} onChange={set("description")} rows={3} className="lux-input mt-1" /></label>
              <label className="block"><span className="text-xs text-slate-400">Accreditation</span>
                <select data-testid="school-form-verify" value={f.verify_status} onChange={set("verify_status")} className="lux-input mt-1">
                  <option value="verify">Not yet verified</option><option value="verified">Verified in Dept. of Education database</option>
                </select></label>
              <label className="flex items-center gap-2 text-sm"><input data-testid="school-form-active" type="checkbox" checked={f.active} onChange={set("active")} />Show to members</label>
              <button data-testid="school-form-submit" className="btn-gold w-full py-3">Save school</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
